'use client';

/**
 * Conversion Funnel Visualization — main flow + OTP verification branch.
 * Main flow: Started → Enrolled → Payment Completed
 * OTP branch: OTP Sent → OTP Verified (branching from Started, for existing contacts)
 * Feature: 014-customer-enrolment-platform (Phase 5, T087)
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@kit/ui/card';
import { ArrowRight, ArrowDown, ShieldCheck } from 'lucide-react';

import type { FunnelStage } from '../_lib/types';

interface ConversionFunnelProps {
  stages: FunnelStage[];
}

// Map stage keys to their flow grouping
const MAIN_FLOW_STAGES = ['started', 'enrolled', 'payment_completed'];
const OTP_STAGES = ['otp_sent', 'otp_verified'];

const STAGE_COLORS: Record<string, string> = {
  started: 'bg-blue-500',
  enrolled: 'bg-emerald-500',
  payment_completed: 'bg-amber-500',
  otp_sent: 'bg-violet-400',
  otp_verified: 'bg-violet-500',
};

function FunnelBar({
  stage,
  maxCount,
  colorClass,
}: {
  stage: FunnelStage;
  maxCount: number;
  colorClass: string;
}) {
  const widthPercent = Math.max((stage.eventCount / maxCount) * 100, 10);

  return (
    <div className="flex-1 space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium">{stage.label}</span>
        <span className="tabular-nums font-semibold">
          {stage.eventCount.toLocaleString()}
        </span>
      </div>
      <div className="h-10 w-full rounded-md bg-muted">
        <div
          className={`h-full rounded-md transition-all ${colorClass}`}
          style={{ width: `${widthPercent}%` }}
        />
      </div>
    </div>
  );
}

export function ConversionFunnel({ stages }: ConversionFunnelProps) {
  const stageMap = new Map(stages.map((s) => [s.stage, s]));
  const mainStages = MAIN_FLOW_STAGES.map((key) => stageMap.get(key)).filter(Boolean) as FunnelStage[];
  const otpStages = OTP_STAGES.map((key) => stageMap.get(key)).filter(Boolean) as FunnelStage[];
  const maxCount = Math.max(...stages.map((s) => s.eventCount), 1);

  const hasOtpData = otpStages.some((s) => s.eventCount > 0);

  if (stages.length === 0 || stages.every((s) => s.eventCount === 0)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Conversion Funnel</CardTitle>
          <CardDescription>No enrolment data for this period</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  // Compute drop-off between main flow stages
  const mainDropOffs = mainStages.map((stage, i) => {
    if (i === 0) return null;
    const prev = mainStages[i - 1]!.eventCount;
    if (prev === 0) return null;
    return Math.round(((prev - stage.eventCount) / prev) * 100);
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Conversion Funnel</CardTitle>
        <CardDescription>
          Enrolment flow progression and verification rates
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Main flow: Started → Enrolled → Payment Completed */}
        <div>
          {/* Desktop */}
          <div className="hidden gap-2 md:flex md:items-center">
            {mainStages.map((stage, index) => (
              <div key={stage.stage} className="flex flex-1 items-center gap-2">
                <div className="flex-1 space-y-1.5">
                  <FunnelBar
                    stage={stage}
                    maxCount={maxCount}
                    colorClass={STAGE_COLORS[stage.stage] ?? 'bg-zinc-400'}
                  />
                  {mainDropOffs[index] !== null && mainDropOffs[index] !== undefined && (
                    <p className="text-center text-[10px] text-muted-foreground">
                      {mainDropOffs[index]! > 0
                        ? `−${mainDropOffs[index]}%`
                        : mainDropOffs[index] === 0
                          ? '0% drop'
                          : `+${Math.abs(mainDropOffs[index]!)}%`}
                    </p>
                  )}
                </div>
                {index < mainStages.length - 1 && (
                  <ArrowRight className="mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground/40" />
                )}
              </div>
            ))}
          </div>

          {/* Mobile */}
          <div className="space-y-3 md:hidden">
            {mainStages.map((stage, index) => {
              const widthPercent = Math.max((stage.eventCount / maxCount) * 100, 10);
              return (
                <div key={stage.stage} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium">{stage.label}</span>
                    <div className="flex items-center gap-2">
                      {mainDropOffs[index] !== null && mainDropOffs[index] !== undefined && mainDropOffs[index]! > 0 && (
                        <span className="text-red-400">−{mainDropOffs[index]}%</span>
                      )}
                      <span className="tabular-nums font-semibold">
                        {stage.eventCount.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div className="h-7 w-full rounded bg-muted">
                    <div
                      className={`h-full rounded transition-all ${STAGE_COLORS[stage.stage] ?? 'bg-zinc-400'}`}
                      style={{ width: `${widthPercent}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* OTP Verification Branch */}
        {hasOtpData && (
          <div className="rounded-lg border border-dashed border-violet-500/30 bg-violet-500/5 p-4">
            <div className="mb-3 flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-violet-500" />
              <span className="text-xs font-medium text-violet-600 dark:text-violet-400">
                Identity Verification (existing contacts only)
              </span>
              <ArrowDown className="h-3 w-3 text-muted-foreground/40" />
            </div>
            <div className="flex items-center gap-2">
              {otpStages.map((stage, index) => (
                <div key={stage.stage} className="flex flex-1 items-center gap-2">
                  <FunnelBar
                    stage={stage}
                    maxCount={maxCount}
                    colorClass={STAGE_COLORS[stage.stage] ?? 'bg-zinc-400'}
                  />
                  {index < otpStages.length - 1 && (
                    <ArrowRight className="mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground/40" />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Legend */}
        {!hasOtpData && (
          <p className="text-xs text-muted-foreground">
            OTP verification is triggered for existing contacts. No OTP events in this period.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
